import {
  ColumnFiltersState,
  SortingState,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TableSkeletonLoader } from '../../../components/SkeletonLoader/Table.SkeletonLoader';
import { useUsersTableColumns } from '../hooks/useUsersTableColumns';
import { useCallback, useMemo, useState } from 'react';
import { Button, styled, useTheme } from '@mui/material';
import { TableV1 } from '../../../components/Table/TableV1';
import { SearchInput } from '../../../components/SearchInput/SearchInput';
import { SingleSelect } from '../../../components/SingleSelect/SingleSelect';
import { ShowInactiveToggle } from '../../../components/ShowInactiveToggle/ShowInactiveToggle';
import { SelectItem, UserRole } from '../../../types';
import { Typography } from '../../../components/Typography/Typography';
import { useUsers } from '../../../queries/hooks/useUsers';
import { roleLabel } from '../../../utils/roleLabels';
import { rolesOf } from '../../../utils/roles';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';

const Wrapper = styled('div')``;

const FiltersSection = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
  gap: 12px;
`;

export const UsersTable = () => {
  const [showInactive, setShowInactive] = useState(false);
  // Fetch everyone and filter here, so the toggle can show the inactive count without a second request.
  const { isLoading: areUsersLoading, data: users } = useUsers('all');
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);
  const columns = useUsersTableColumns();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [selectedTradeStatus, setSelectedTradeStatus] = useState<SelectItem | null>(null);

  const inactiveCount = useMemo(
    () => (users ?? []).filter((user) => user.isActive === false).length,
    [users]
  );

  const visibleUsers = useMemo(
    () =>
      (users ?? [])
        .filter((user) => showInactive || user.isActive !== false)
        .sort((a, b) => Number(a.isActive === false) - Number(b.isActive === false)),
    [users, showInactive]
  );

  const table = useReactTable({
    data: visibleUsers,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    enableSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getRowId: (user) => String(user.id),
  });

  const sortedUniqueUserRoles = useMemo(() => {
    // Multi-role: collect every role held across users (not just the legacy scalar) so the filter lists
    // all roles a Manager-Submitter / role+Admin user carries. Filter matches membership on the raw role id.
    const roleSet = new Set<UserRole>();
    visibleUsers.forEach((user) => rolesOf(user).forEach((role) => roleSet.add(role)));
    return Array.from(roleSet)
      .map((value) => ({ id: value, value: roleLabel(value) }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [visibleUsers]);

  const onChangeUserRoleFilter = useCallback(
    (userRole: SelectItem | null) => {
      setSelectedTradeStatus(userRole);
      table.getColumn('roles')?.setFilterValue(userRole?.id ?? '');
    },
    [table]
  );

  const isResetDisabled = useMemo(() => {
    if (columnFilters.length === 0 && !showInactive) return true;
    return false;
  }, [columnFilters, showInactive]);

  const onFiltersReset = useCallback(() => {
    table.getColumn('name')?.setFilterValue('');
    table.getColumn('roles')?.setFilterValue('');
    setSelectedTradeStatus(null);
    setShowInactive(false);
  }, [table]);

  if (areUsersLoading || !users) return <TableSkeletonLoader />;

  return (
    <Wrapper>
      <FiltersSection>
        <SearchInput
          placeholder='Search'
          style={{ width: isSmallScreen ? '150px' : '370px', height: '36px' }}
          onClear={() => table.getColumn('name')?.setFilterValue('')}
          onChange={(e) => table.getColumn('name')?.setFilterValue(e.target.value)}
          value={table.getColumn('name')?.getFilterValue() ?? ''}
        />
        <SingleSelect
          style={{ width: isSmallScreen ? '150px' : '210px' }}
          listboxStyle={{ height: '195px' }}
          options={sortedUniqueUserRoles}
          value={selectedTradeStatus}
          onChange={(_, type) => onChangeUserRoleFilter(type)}
          disablePortal
          fieldPlaceholder='User Type'
        />
        <ShowInactiveToggle
          checked={showInactive}
          onChange={setShowInactive}
          label='Show inactive'
          count={inactiveCount}
        />
        <Button
          onClick={onFiltersReset}
          variant='text'
          style={{ height: '28px', marginTop: '2px' }}
          disabled={isResetDisabled}
          sx={{
            marginLeft: 'auto',
            '.MuiButton-startIcon': { marginRight: '2px' },
            '&.Mui-disabled': { opacity: 0.4, cursor: 'not-allowed', pointerEvents: 'auto' },
          }}
        >
          <Typography variant='subtitle1' color={colors.textAccent.default}>
            Reset Filters
          </Typography>
        </Button>
      </FiltersSection>
      <TableV1
        table={table}
        height='auto'
        getRowClassName={(row) => (row.original.isActive === false ? 'muted-row' : undefined)}
        wrapperStyle={{
          maxHeight: 'calc(100vh - 288px)',
          overflowX: 'scroll',
          border: `1px solid ${colors.border.default}`,
        }}
      />
    </Wrapper>
  );
};
